import { Fragment, useMemo, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { DataGrid, KeenIcon } from '@/components';
import { useNavigate } from 'react-router-dom';
import { useGetEnvironmentVariablesQuery } from '@/services/EnvironmentVariablesService';
import { EnvironmentVariable } from '@/types/environment-variables.types';
import { PaginationState } from '@tanstack/react-table';
import { useUpdateEnvironmentVariableMutation } from '@/services/EnvironmentVariablesService';
import {
  ExternalServiceType,
  ModeType,
} from '@/types/environment-variables.types';
import { useSnackbar } from 'notistack';
import { formatDisplayDateTime } from '@/utils/Date';

const EnvironmentVariablesListing = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const [updateEnvironmentVariable] = useUpdateEnvironmentVariableMutation();

  const { data, isLoading, refetch } = useGetEnvironmentVariablesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'service_name',
        header: 'Name',
        cell: ({ row }: { row: { original: EnvironmentVariable } }) => (
          <span className="font-medium text-gray-800">
            {row.original.service_name}
          </span>
        ),
      },
      {
        accessorKey: 'provider',
        header: 'Provider',
        cell: ({ row }: { row: { original: EnvironmentVariable } }) => (
          <span className="text-gray-700">{row.original.provider}</span>
        ),
      },
      {
        id: 'value',
        header: 'Value',
        cell: () => (
          <>
            <span className="sr-only">Hidden value</span>
            <span aria-hidden="true" className="font-mono tracking-widest">
              ********
            </span>
          </>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        cell: ({ row }: { row: { original: EnvironmentVariable } }) => (
          <span className="text-gray-600">
            {row.original.changed_at || row.original.updated_at
              ? formatDisplayDateTime(
                row.original.changed_at || row.original.updated_at,
                { hour12: false }
              )
              : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: { original: EnvironmentVariable } }) => {
          const isStripe =
            row.original.service_type ===
            ExternalServiceType.STRIPE_PAYMENT_OPTION;
          const isPaytrail =
            row.original.service_type ===
            ExternalServiceType.PAYTRAIL_PAYMENT_OPTION;

          const isPaymentOption = isStripe || isPaytrail;

          const handleToggle = async (checked: boolean) => {
            if (!checked) {
              // User is trying to disable. Check if the other one is also disabled.
              const otherType = isStripe
                ? ExternalServiceType.PAYTRAIL_PAYMENT_OPTION
                : ExternalServiceType.STRIPE_PAYMENT_OPTION;

              const otherOption = data?.data.find(
                (item) => item.service_type === otherType
              );

              // If we found the other option and it's already disabled, or if we can't find it
              // (which shouldn't happen if they are on the same page, but for safety...)
              if (otherOption && otherOption.mode_type === ModeType.DISABLED) {
                enqueueSnackbar(
                  'At least one payment option must be enabled. Cannot disable both.',
                  { variant: 'warning' }
                );
                return;
              }
            }

            try {
              await updateEnvironmentVariable({
                id: row.original.id,
                data: {
                  mode_type: checked ? ModeType.ENABLED : ModeType.DISABLED,
                },
              }).unwrap();
              enqueueSnackbar(
                `${row.original.service_name} ${checked ? 'enabled' : 'disabled'} successfully`,
                { variant: 'success' }
              );
              refetch();
            } catch {
              enqueueSnackbar('Failed to update payment option status', {
                variant: 'error',
              });
            }
          };

          return (
            <div className="flex items-center gap-2">
              {isPaymentOption ? (
                <label className="switch switch-sm">
                  <span className="sr-only">
                    Toggle status for {row.original.service_name}
                  </span>
                  <input
                    type="checkbox"
                    checked={row.original.mode_type === ModeType.ENABLED}
                    onChange={(e) => handleToggle(e.target.checked)}
                    aria-label={`Toggle status for ${row.original.service_name}`}
                  />
                </label>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/configuration/environment-variables/edit/${row.original.id}`
                    )
                  }
                  title="Edit environment variable"
                  aria-label={`Edit environment variable ${row.original.service_name}`}
                >
                  <KeenIcon icon="pencil" />
                </button>
              )}
            </div>
          );
        },
        meta: { className: 'w-20' },
      },
    ],
    [navigate, updateEnvironmentVariable, data?.data, enqueueSnackbar]
  );

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Environment Variables" />
              <ToolbarDescription>
                Manage environment variables used by the platform.
              </ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <KeenIcon
                icon="magnifier"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <label htmlFor="env-vars-search" className="sr-only">
                Search environment variables by name
              </label>
              <input
                type="text"
                placeholder="Search by name..."
                className="input input-sm pl-10 w-64 h-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                id="env-vars-search"
                aria-label="Search environment variables by name"
              />
            </div>
          </div>
        </div>

        <div>
          <DataGrid
            columns={columns}
            data={data?.data || []}
            loading={isLoading}
            serverSide
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              totalItems: data?.total || 0,
              totalPages: data?.totalPages || 0,
              setPagination,
            }}
          />
        </div>
      </div>
    </Fragment>
  );
};

export { EnvironmentVariablesListing };
